// Inventory · Purchasing.
// Answers: what do I need to order, from whom, and what is on order?
//
// Three tiers by how sure the quantity is. Firm lines (the job has a locked revision) are
// black: buy them. Committed lines (won, not yet locked) are grey and sit in the same vendor
// groups, so an order being built for one job shows what is coming for the others.
// Nothing shows here before a job is won.
//
// Buying is always a purchase order: one click on a vendor's group makes the order (or adds
// to that vendor's draft), and the order carries the number, the lines and the state.

import { useState, type ReactNode } from 'react';
import { deleteRow, toast, type Row } from '../../lib/api';
import { money, qty, shortDate } from '../../lib/format';
import { draftFor, lineRequests, pullEarly, pullOpen, putOnOrder, undoPull, useInventory, useVendors, type OrderRequest } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Btn, Empty, Info, Ln, Loading, Money, Panel, Seg, Tag } from '../../ui/kit';
import { FreeReceipt } from './FreeReceipt';
import { PurchaseOrder } from './PurchaseOrder';
import { useInventoryNames, useOrderStateNames } from './names';

interface Entry {
  key: string;
  group: string;
  vendorId: string | null;
  /** Black lines are to act on; grey ones are only shown. */
  black: boolean;
  firm: boolean;
  /** What an order would take from this line. */
  requests: OrderRequest[];
  cents: number;
  copy: string | null;
  node: ReactNode;
}

const NO_VENDOR = 'No vendor named';
const STOCK_GROUP = 'Stock reorder';

export function Purchasing({ openMaterial }: { openMaterial: (id: string) => void }) {
  const s = useSession();
  const names = useInventoryNames();
  const stateNames = useOrderStateNames();
  const vendors = useVendors();
  const inv = useInventory();
  const [by, setBy] = useState<'vendor' | 'job'>('vendor');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [closedToo, setClosedToo] = useState(false);
  const [free, setFree] = useState(false);
  const canBuy = s.canEdit('screen.purchasing');

  if (inv.loading) return <Loading what="Working out what to buy" />;

  const price = (m: Row) => (m.price === null || m.price === undefined ? null : Number(m.price));
  const name = (m: Row) => (
    <button type="button" className="linklike read" onClick={() => openMaterial(m.id)}>
      {m.description}
    </button>
  );
  // a click adds to the vendor's newest draft, or starts a new numbered order
  const order = async (vendorId: string | null, requests: OrderRequest[]) => {
    if (!vendorId || requests.length === 0) return;
    const id = await putOnOrder(vendorId, requests, draftFor(inv, vendorId)?.po.id);
    setOrderId(id);
  };
  const orderButton = (e: Entry) =>
    canBuy && e.requests.length > 0 && e.vendorId ? (
      <Btn small onClick={() => void order(e.vendorId, e.requests)}>
        Order
      </Btn>
    ) : null;

  const entries: Entry[] = [];

  // ---- job lines ----
  for (const l of inv.lines) {
    const gap = Number(l.buy);
    const firm = l.tier === 'firm';
    const backordered = l.ordered.filter((o) => Number(o.qty) > Number(o.received));
    const actionable = gap > 0 || l.waiting.length > 0;
    if (!actionable && !(by === 'job' && backordered.length > 0)) continue;
    const m = l.material;
    const black = firm || l.waiting.length > 0;
    const waitingQty = l.waiting.reduce((n, r) => n + Number(r.qty), 0);
    const toOrder = (firm ? gap : 0) + waitingQty;
    const requests = lineRequests(l);
    const e: Entry = {
      key: `${l.job_id}|${l.material_id}`,
      group: by === 'vendor' ? vendors.name(m.vendor_id) ?? NO_VENDOR : l.jobName,
      vendorId: m.vendor_id ?? null,
      black,
      firm,
      requests,
      cents: Math.round(toOrder * (l.est_unit_cents ?? 0)),
      copy: toOrder > 0 ? `${qty(toOrder)} ${m.unit ?? ''}  ${m.description}${m.part_number ? `  (${m.part_number})` : ''}${l.links.map((k) => `  ${k.number}: ${k.url}`).join('')}` : null,
      node: null,
    };
    e.node = (
      <Ln
        key={e.key}
        className={black ? '' : 'grey'}
        left={
          <>
            {name(m)} · need {qty(l.need)}
            {Number(l.consumed) > 0 && `, used ${qty(l.consumed)}`}
            {Number(l.from_stock) > 0 && `, from stock ${qty(l.from_stock)}`}
            {l.ordered.map((o) => (
              <span key={o.request.id}>
                , {qty(o.qty)} on{' '}
                <button type="button" className="linklike read" onClick={() => setOrderId(o.order.po.id)}>
                  {o.order.po.number ?? 'a receipt'}
                </button>
                {Number(o.received) > 0 && ` (${qty(o.received)} in)`}
              </span>
            ))}{' '}
            {toOrder > 0 ? (
              <>
                → to order <b>{qty(toOrder)}</b> {m.unit ?? ''}
                {!firm && gap > 0 && `, ${qty(gap)} more not pulled`}
              </>
            ) : gap > 0 ? (
              <>
                → to buy <b>{qty(gap)}</b> {m.unit ?? ''}
              </>
            ) : (
              '→ nothing more to order'
            )}
            <br />
            <small>
              {by === 'vendor' ? l.jobName : vendors.name(m.vendor_id) ?? 'no vendor'} <Tag kind={firm ? 'on' : 'off'}>{firm ? names.firm : names.committed}</Tag> {l.pulled && <Tag kind="un">pulled early</Tag>}
              {s.can('see_prices') && l.est_unit_cents !== null && ` · ${money(l.est_unit_cents)} each${l.est_from_job ? ', as typed on the job' : ''}`}
              {l.est_unit_cents === null && ' · no price'} {!m.verified && <Tag kind="un">unverified</Tag>}
              {l.links.map((k) => (
                <span key={`${k.number}|${k.url}`}>
                  {' '}
                  · {k.number}:{' '}
                  <a href={k.url} target="_blank" rel="noopener noreferrer" title={k.url}>
                    vendor page
                  </a>
                </span>
              ))}
            </small>
          </>
        }
        right={
          <span className="nowrap">
            {!black && <small>not yet locked </small>}
            {canBuy && l.rows.some(pullOpen) && (
              <>
                <button type="button" className="linklike" onClick={() => void undoPull(l)}>
                  Undo pull
                </button>{' '}
              </>
            )}
            {canBuy && !firm && gap > 0 && (
              <>
                <Btn small onClick={() => void pullEarly(l)}>
                  {l.pulled ? `Pull ${qty(gap)} more` : 'Pull in'}
                </Btn>{' '}
              </>
            )}
            {orderButton(e)}
          </span>
        }
      />
    );
    entries.push(e);
  }

  // ---- stock: under safety stock (worked out now), or asked for by a scan (stored) ----
  const stockEntry = (m: Row, key: string, amount: number, why: ReactNode, request: OrderRequest, dismiss?: () => void) => {
    const e: Entry = {
      key,
      group: by === 'vendor' ? vendors.name(m.vendor_id) ?? NO_VENDOR : STOCK_GROUP,
      vendorId: m.vendor_id ?? null,
      black: true,
      firm: true,
      requests: [request],
      cents: Math.round(amount * (price(m) ?? 0) * 100),
      copy: `${amount > 0 ? qty(amount) : '?'} ${m.unit ?? ''}  ${m.description}${m.part_number ? `  (${m.part_number})` : ''}`,
      node: null,
    };
    e.node = (
      <Ln
        key={key}
        left={
          <>
            {name(m)} → to order <b>{amount > 0 ? qty(amount) : 'set on the order'}</b> {m.unit ?? ''}
            <br />
            <small>
              {by === 'job' && `${vendors.name(m.vendor_id) ?? 'no vendor'} `}
              <Tag kind="un">reorder</Tag> {why}
              {s.can('see_prices') && price(m) !== null && ` · ${money(Math.round(price(m)! * 100))} each`}
            </small>
          </>
        }
        right={
          <span className="nowrap">
            {canBuy && dismiss && (
              <>
                <button type="button" className="linklike" onClick={dismiss}>
                  Dismiss
                </button>{' '}
              </>
            )}
            {orderButton(e)}
          </span>
        }
      />
    );
    entries.push(e);
  };
  for (const r of inv.reorders) {
    const m = inv.materialById.get(r.material_id)!;
    stockEntry(m, `reorder|${m.id}`, Number(r.qty), `${qty(r.on_hand)} on hand${Number(r.on_order) > 0 ? ` + ${qty(r.on_order)} on order` : ''}, under the ${names.safety_stock} of ${qty(r.safety_stock)}`, {
      kind: 'reorder', material_id: m.id, qty: Number(r.qty), est_unit_price: price(m), note: `under ${names.safety_stock}`,
    });
  }
  for (const r of inv.waitingReorders) {
    const m = inv.materialById.get(r.material_id)!;
    stockEntry(m, `asked|${r.id}`, Number(r.qty), `${r.note ?? 'asked for'} · ${shortDate(r.created_at)}`, { purchase_line_id: r.id, material_id: m.id, est_unit_price: price(m) }, () => void deleteRow('purchase_line', r.id).then(() => toast('Reorder request dismissed.')));
  }

  const groups = new Map<string, Entry[]>();
  for (const e of entries) groups.set(e.group, [...(groups.get(e.group) ?? []), e]);
  const firmCount = entries.filter((e) => e.black).length;
  const greyCount = entries.length - firmCount;
  const shownOrders = inv.orders.filter((o) => closedToo || o.state !== 'closed');
  const closedCount = inv.orders.filter((o) => o.state === 'closed').length;

  return (
    <div className="stack">
      <div className="row mid" style={{ justifyContent: 'space-between' }}>
        <small>
          <b>{firmCount}</b> to order · <span className="muted">{greyCount} {names.committed}, not yet locked</span>
          <Info>
            A <b>{names.firm}</b> line belongs to a job with a locked revision: its quantity is frozen, so buy it. A <b>{names.committed}</b> line belongs to a job that is won but not yet locked: it is shown grey beside the others so you can see what is
            coming. <b>Pull in</b> puts a grey line on the buy list now, for a long-lead item that cannot wait for the lock; when the job locks, what was pulled already counts as covered. A pull can be undone in one click until it is on an order. Jobs that are
            not won yet never appear here; they are only a forecast, under {names.supply_demand}.
            <br />
            <br />
            Every purchase is an order. <b>Create PO</b> on a vendor puts all of that vendor's black lines on one numbered order (or adds them to the draft that vendor already has); <b>Order</b> does the same for one line.
          </Info>
        </small>
        <Seg items={[{ key: 'vendor', label: 'By vendor' }, { key: 'job', label: 'By job' }]} value={by} onChange={setBy} />
      </div>

      {entries.length === 0 && <Empty>Nothing to order. Lines appear here once a job is won ({names.committed}, grey), become {names.firm} when its revision is locked, and leave when they are on an order.</Empty>}
      {[...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([group, all]) => {
        // black first, then grey, in the same group
        const es = [...all].sort((a, b) => Number(!a.black) - Number(!b.black));
        const orderable = es.filter((e) => e.requests.length > 0);
        const total = orderable.reduce((n, e) => n + e.cents, 0);
        const vendorId = by === 'vendor' ? es[0]!.vendorId : null;
        const draft = draftFor(inv, vendorId);
        return (
          <Panel
            key={group}
            label={
              <>
                {group} · {es.length} {es.length === 1 ? 'line' : 'lines'} · <Money cents={total} /> to order
              </>
            }
            right={
              <span className="nowrap">
                {by === 'vendor' && canBuy && orderable.length > 0 && (
                  <>
                    <Btn
                      small
                      read
                      onClick={() => {
                        const text = orderable.map((e) => e.copy).filter(Boolean).join('\n');
                        void navigator.clipboard.writeText(`${group}\n${text}`).then(() => toast('Vendor list copied.'));
                      }}
                    >
                      Copy list
                    </Btn>{' '}
                    {vendorId ? (
                      <Btn small primary onClick={() => void order(vendorId, orderable.flatMap((e) => e.requests))}>
                        {draft ? `Add to ${draft.po.number}` : 'Create PO'}
                      </Btn>
                    ) : (
                      <small>name a vendor on these to order them</small>
                    )}
                  </>
                )}
              </span>
            }
          >
            {es.map((e) => e.node)}
          </Panel>
        );
      })}

      <Panel
        label={`Purchase orders (${shownOrders.length})`}
        right={
          <span className="row mid" style={{ gap: 8 }}>
            {closedCount > 0 && (
              <label className="check">
                <input type="checkbox" className="read" checked={closedToo} onChange={(e) => setClosedToo(e.target.checked)} /> show {closedCount} {stateNames.closed.toLowerCase()}
              </label>
            )}
            {s.can('screen.receiving') && (
              <Btn small title="Stock in with no order behind it: something bought in person." onClick={() => setFree(true)}>
                Free receipt
              </Btn>
            )}
          </span>
        }
      >
        {shownOrders.length === 0 && <Empty>No orders yet. An order is made from the lines above, one vendor at a time.</Empty>}
        {[...shownOrders].reverse().map((o) => {
          const expected = o.lines.map((l) => (Number(l.outstanding) > 0 ? l.expected_on : null)).filter(Boolean).sort() as string[];
          return (
            <Ln
              key={o.po.id}
              onClick={() => setOrderId(o.po.id)}
              left={
                <>
                  <b>{o.po.number ?? 'Receipt'}</b> · {o.vendor?.name ?? 'no vendor'} · {o.lines.length} {o.lines.length === 1 ? 'line' : 'lines'}
                  <br />
                  <small>
                    {o.po.placed_at ? `placed ${shortDate(o.po.placed_at)}` : `started ${shortDate(o.po.created_at)}`}
                    {expected.length > 0 && ` · expected ${shortDate(expected[expected.length - 1])}`}
                    {o.state === 'partly_received' && ` · ${o.lines.filter((l) => Number(l.outstanding) > 0).length} backordered`}
                  </small>
                </>
              }
              right={
                <span className="nowrap">
                  <Money cents={o.est_cents} /> <Tag kind={o.state === 'draft' ? 'un' : o.state === 'closed' ? 'off' : 'on'}>{stateNames[o.state]}</Tag>
                </span>
              }
            />
          );
        })}
      </Panel>

      {orderId && <PurchaseOrder orderId={orderId} onClose={() => setOrderId(null)} openMaterial={openMaterial} />}
      {free && <FreeReceipt onClose={() => setFree(false)} onDone={setOrderId} />}
    </div>
  );
}
